  ```vue
<template>
  <div class="modal-card-body">      
    <!-- Wrap the image and the table in a new div -->
    <div :class="['results-container', $mq]">
      <div class="table-container">
        <div class="container mt-2">
          <b-table
            :mobile-cards="false"
            :data="shapes"                    
            :row-class="(row, index) => row.convertedCalculated < 0 && 'is-danger'">
            <b-table-column field="displayIndex" label="#" v-slot="props">
              {{ props.row.displayIndex }}
            </b-table-column>
            <b-table-column field="labelText" label="Label" v-slot="props">
              {{ props.row.labelText }}
            </b-table-column>
            <b-table-column field="convertedCalculated" label="Measurement" v-slot="props">
              {{ props.row.convertedCalculated.toLocaleString() }}
            </b-table-column>
            <b-table-column field="suffix" label="Unit" v-slot="props">
              {{ props.row.suffix }}
            </b-table-column>
          </b-table>
        </div>          
      </div>
      <div class="total-area">
        <p class="result"><vector-polygon-icon />Total Area: {{ totalShapeCalculatedConverted.toLocaleString() }} {{ measureSuffix }}</p>
      </div>                
      <p class="estimate-note"><information-icon />{{ config.verbiage.results_estimate_notice }}</p>
      <div class="what-next" v-html="whatNextParagraph"></div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMainStore } from '@/stores/main'
import { useMapSharingStore } from '@/stores/mapSharing'
import { useContactStore } from '@/stores/contact'
import { useDialogsStore } from '@/stores/dialogs'
import { useConfig } from '@/composables/useConfig'
import LeadWorkflowNavigationButtons from '@/components/lead_workflow_components/LeadWorkflowNavigationButtons.vue'

export default {
  name: 'ContactInfo',
  components: {
    LeadWorkflowNavigationButtons
  },
  props: {
    nextButtonClicked: {
      type: Boolean,
      default: false
    },
    prevButtonClicked: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    'set-title',
    'set-next-button-label',
    'set-next-button-icon',
    'set-show-next',
    'set-prev-button-label',
    'set-show-prev',
    'set-prev-route',
    'set-next-button-enabled',
    'set-prev-button-enabled'
  ],
  setup(props, { emit }) {
    const router = useRouter()
    const mainStore = useMainStore()
    const mapSharingStore = useMapSharingStore()
    const contactStore = useContactStore()
    const dialogsStore = useDialogsStore()
    const { config, verbiage } = useConfig()

    const title = ref('Your results are ready!')
    const columns = ref([
      {
        field: 'displayIndex',
        label: '#'
      },
      {
        field: 'labelText',
        label: 'Label'
      },
      {
        field: 'convertedCalculated',
        label: 'Measurement'
      },
      {
        field: 'suffix',
        label: 'Unit'
      }
    ])

    // Computed
    const shapes = computed(() => mainStore.shapesWithConversions)
    const sharingLink = computed(() => mapSharingStore.sharingLink)
    const mapScreenshotUrl = computed(() => mapSharingStore.mapScreenshotUrl)
    const productInterest = computed(() => contactStore.productInterest)
    const totalShapeCalculatedConverted = computed(() => mainStore.totalShapeCalculatedConverted)
    const measureSuffix = computed(() => mainStore.measureSuffix)
    const meta = computed(() => mainStore.meta)

    const whatNextParagraph = computed(() => {
      return config.value.verbiage.results_what_next_paragraph.replace('{{ customerEmail }}', contactStore.email)
    })

    // Methods
    const startOver = async () => {
      try {
        mainStore.setLoading(true)
        await mainStore.resetState()
        router.push('/flow/introduction')
      } finally {
        mainStore.setLoading(false)
      }
    }

    const confirmStartOver = () => {
      dialogsStore.confirm(0, `Starting over will remove all measurements. Your results will only be available in the email you received.<br>
        <br><span class="has-text-weight-bold">Are you sure?</span>`, {
        title: 'Warning',
        confirmText: 'Yes, start over',
        cancelText: 'Cancel',
        type: 'is-primary',
        onConfirm: startOver
      })
    }

    const viewOnMap = () => {
      console.log(sharingLink.value)
      window.open(sharingLink.value, '_blank')
    }

    // Watchers
    watch(() => props.nextButtonClicked, (val) => {
      if (val) {
        viewOnMap()
      }
    })

    watch(() => props.prevButtonClicked, (val) => {
      if (val) {
        confirmStartOver()
      }
    })

    // Lifecycle
    onMounted(() => {
      emit('set-title', verbiage.value.results_title)
      emit('set-next-button-label', 'VIEW ON MAP')
      emit('set-next-button-icon', 'map-icon')
      emit('set-show-next', true)
      emit('set-prev-button-label', 'Start Over')
      emit('set-show-prev', true)
      emit('set-prev-route', '')
      emit('set-next-button-enabled', true)
      emit('set-prev-button-enabled', true)
    })

    return {
      title,
      columns,
      shapes,
      sharingLink,
      mapScreenshotUrl,
      productInterest,
      totalShapeCalculatedConverted,
      measureSuffix,
      meta,
      config,
      whatNextParagraph,
      confirmStartOver,
      viewOnMap
    }
  }
}
</script>

<style>
  div.total-area {
    border: 1px solid #ddd;
    padding: 10px;
    margin-top: 10px;
    border-radius: 5px;
    background-color: #f9f9f9;
  }

  
  .vector-polygon-icon {
    margin-right: 5px;
  }

  div.total-area p.result {
    font-size: 1.1em;
    font-weight: bold;
    
    text-align: center;
    color: #485e2f;
    
    text-shadow: 0 0 10px #ccc;
  }

  p.estimate-note {
    font-size: 0.8em;
    margin-top: 10px;
  }

  div.what-next {
    margin-top: 15px;
    padding: 10px 10px 0;
    border-top: 1px solid #ccc;
  }
  b-table {
    position: relative;
  }

  b-table template[slot="footer"] {
    position: sticky;
    bottom: 0;
    background-color: inherit;
  }

  tr.is-danger {
    background: #ff3860;
    color: #fff;
  }
</style>